import {DropEvent, FileRejection, useDropzone} from 'react-dropzone';
import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {
    Button,
    Icon,
    Icons,
    IconSizes,
    Typography,
} from '@toppy-webshop/toppy-design-system';

export interface DropzoneProps {}

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
};

const focusedStyle = {
    borderColor: '#ffcb01',
};

const acceptStyle = {
    borderColor: '#00e676',
};

const rejectStyle = {
    borderColor: '#ff1744',
};

interface CustomDropzoneProps {
    onChange?: (files: File[]) => void;
}

const Dropzone = ({onChange}: CustomDropzoneProps) => {
    const [myFiles, setMyFiles] = useState<File[]>([]);

    const onDrop = useCallback<
        <T extends File>(
            acceptedFiles: T[],
            fileRejections: FileRejection[],
            event: DropEvent
        ) => void
    >(
        acceptedFiles => {
            let newFiles = [...myFiles, ...acceptedFiles];
            if (newFiles.length > 5) {
                newFiles = newFiles.slice(0, 5);
            }

            setMyFiles(newFiles);
            if (onChange) {
                onChange(newFiles);
            }
        },
        [myFiles, onChange]
    );
    const {getRootProps, getInputProps, isFocused, isDragAccept, isDragReject} =
        useDropzone({
            accept: {'image/*': []},
            multiple: true,
            maxSize: 5242880,
            onDrop,
        });

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    const removeFile = (file: File) => {
        const newFiles = [...myFiles];
        newFiles.splice(newFiles.indexOf(file), 1);
        setMyFiles(newFiles);
        if (onChange) {
            onChange(newFiles);
        }
    };

    return (
        <>
            <div
                {...getRootProps({
                    style,
                } as React.HTMLAttributes<HTMLElement>)}
            >
                <input {...getInputProps()} />
                <Icon icon={Icons.DOWNLOAD} size={IconSizes.JUMBO} />
                <Typography>Plaats je foto's hier</Typography>
                <br />
                <Button
                    type={'outline'}
                    label={'Kies uit je bestanden'}
                    onClick={e => {
                        e.preventDefault();
                    }}
                />
                <Typography size={'xs'}>
                    Maximaal 5 bestanden, 5Mb per bestand
                </Typography>
            </div>
            <ul>
                {myFiles.map(file => {
                    return (
                        <li>
                            {file.name}{' '}
                            <span
                                onClick={() => removeFile(file)}
                                style={{
                                    color: 'var(--color-red)',
                                    cursor: 'pointer',
                                }}
                            >
                                <Icon icon={Icons.CROSS} />
                            </span>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

export default Dropzone;
